import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, NativeSelect } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const EMPTY = { code: "", name: "", sector_id: "", club: "", discipline_id: "", location: "", start_date: "", end_date: "",
  allowed_category_ids: [], max_participants: 0, status: "Programmata", organizer_ids: [] };

const CheckList = ({ items, selected, onToggle, testPrefix, render }) => (
  <div className="grid max-h-48 gap-2 overflow-y-auto sm:grid-cols-2">
    {items.map((it) => (
      <label key={it.id} className={`flex cursor-pointer items-start gap-2 rounded-lg border p-2.5 text-sm transition-colors ${selected.includes(it.id) ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
        <input type="checkbox" checked={selected.includes(it.id)} onChange={() => onToggle(it.id)} className="mt-0.5" data-testid={`${testPrefix}-${it.code || it.id}`} />
        {render(it)}
      </label>
    ))}
  </div>
);

export function EventForm({ open, onOpenChange, event, onSaved }) {
  const { user } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [lists, setLists] = useState({ sectors: [], disciplines: [], categories: [], organizers: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(event ? { ...EMPTY, ...event } : { ...EMPTY, sector_id: user.sector_id || "" });
    Promise.all([api.get("/sectors"), api.get("/disciplines"), api.get("/categories"), api.get("/users/organizers")]).then(([s, d, c, o]) =>
      setLists({ sectors: s.data, disciplines: d.data, categories: c.data, organizers: o.data }));
  }, [open, event, user.sector_id]);

  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  const toggle = (k) => (id) => setF({ ...f, [k]: f[k].includes(id) ? f[k].filter((x) => x !== id) : [...f[k], id] });
  const catName = (id) => lists.categories.find((c) => c.id === id)?.name;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k]]));
      payload.max_participants = Number(payload.max_participants) || 0;
      const { data } = event ? await api.put(`/events/${event.id}`, payload) : await api.post("/events", payload);
      toast.success(event ? "Manifestazione aggiornata" : "Manifestazione creata");
      onOpenChange(false);
      onSaved?.(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="event-form-dialog">
        <DialogHeader><DialogTitle>{event ? "Modifica manifestazione" : "Nuova manifestazione"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nome evento"><Input required value={f.name} onChange={set("name")} data-testid="event-name-input" /></Field>
            <Field label="Codice manifestazione" hint="Lascia vuoto per generarlo automaticamente"><Input value={f.code} onChange={set("code")} className="font-mono uppercase" data-testid="event-code-input" /></Field>
            <Field label="Settore organizzatore">
              <NativeSelect value={f.sector_id} onChange={set("sector_id")} disabled={user.role !== "admin"} required testid="event-sector-select">
                <option value="">Seleziona…</option>
                {lists.sectors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </NativeSelect>
            </Field>
            <Field label="ASD/SSD organizzatrice"><Input value={f.club} onChange={set("club")} data-testid="event-club-input" /></Field>
            <Field label="Disciplina">
              <NativeSelect value={f.discipline_id} onChange={(v) => setF({ ...f, discipline_id: v, allowed_category_ids: [] })} required testid="event-discipline-select">
                <option value="">Seleziona…</option>
                {lists.disciplines.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </NativeSelect>
            </Field>
            <Field label="Luogo"><Input value={f.location} onChange={set("location")} data-testid="event-location-input" /></Field>
            <Field label="Data inizio"><Input type="date" required value={f.start_date} onChange={set("start_date")} data-testid="event-start-date-input" /></Field>
            <Field label="Data fine"><Input type="date" required value={f.end_date} onChange={set("end_date")} data-testid="event-end-date-input" /></Field>
            <Field label="Numero massimo partecipanti" hint="0 = nessun limite"><Input type="number" min={0} value={f.max_participants} onChange={set("max_participants")} data-testid="event-max-input" /></Field>
            <Field label="Stato manifestazione">
              <NativeSelect value={f.status} onChange={set("status")} testid="event-status-select">
                {["Programmata", "In corso", "Conclusa", "Annullata"].map((s) => <option key={s}>{s}</option>)}
              </NativeSelect>
            </Field>
          </div>
          {f.discipline_id && (
            <div>
              <div className="mb-1 text-sm font-medium text-slate-700">Categorie ammesse</div>
              <div className="mb-2 text-xs text-slate-500">Un atleta è ammesso se possiede una di queste categorie, oppure un'abilitazione che le comprende (corrispondenze configurate in Impostazioni). Il settore di provenienza non è un criterio di esclusione.</div>
              <CheckList items={lists.categories.filter((c) => c.discipline_id === f.discipline_id)} selected={f.allowed_category_ids} onToggle={toggle("allowed_category_ids")} testPrefix="event-category"
                render={(c) => <span><span className="font-medium">{c.name}</span>{c.covers?.length > 0 && <span className="block text-xs text-slate-500">Comprende: {c.covers.map(catName).join(", ")}</span>}</span>} />
            </div>
          )}
          <div>
            <div className="mb-2 text-sm font-medium text-slate-700">Organizzatori autorizzati alle verifiche</div>
            <CheckList items={lists.organizers} selected={f.organizer_ids} onToggle={toggle("organizer_ids")} testPrefix="event-organizer"
              render={(o) => <span><span className="font-medium">{o.name}</span><span className="block text-xs text-slate-500">{o.email}</span></span>} />
          </div>
          <DialogFooter><Button type="submit" disabled={busy || f.allowed_category_ids.length === 0} className="bg-[#0F2042] hover:bg-[#1E3A8A]" data-testid="event-form-submit-btn">Salva manifestazione</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
